import { onPageIn, onMount, onDestroy } from '@/modules/_'
import gsap from 'gsap'

const shared = {
  clonedElement: null as HTMLElement | null,
  currentTestimonial: null as HTMLElement | null,
  mouseX: 0,
  mouseY: 0,
  currentX: 0,
  currentY: 0,
  isHovering: false,
  animationFrame: null as number | null,
  elementWidth: 0,
  elementHeight: 0,
  enterTimeout: null as number | null,
  isAnimating: false,
  oldCloneRef: null as HTMLElement | null,
}

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const testimonials = Array.from(
    element.querySelectorAll('.testimonial_comp')
  ) as HTMLElement[]
  const templateMoreDetails = element.querySelector(
    '.testimonial_more_details'
  ) as HTMLElement

  const isDesktop = window.innerWidth > 767
  const isHoverDevice = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  ).matches

  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor
  }

  const updateElementDimensions = () => {
    if (!shared.clonedElement) return
    const rect = shared.clonedElement.getBoundingClientRect()
    shared.elementWidth = rect.width
    shared.elementHeight = rect.height
  }

  const getConstrainedPosition = (mouseX: number, mouseY: number) => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 20

    let finalX = mouseX
    let finalY = mouseY

    if (finalX - shared.elementWidth / 2 < padding) {
      finalX = padding + shared.elementWidth / 2
    }

    if (finalX + shared.elementWidth / 2 > viewportWidth - padding) {
      finalX = viewportWidth - padding - shared.elementWidth / 2
    }

    if (finalY - shared.elementHeight / 2 < padding) {
      finalY = padding + shared.elementHeight / 2
    }

    if (finalY + shared.elementHeight / 2 > viewportHeight - padding) {
      finalY = viewportHeight - padding - shared.elementHeight / 2
    }

    return { x: finalX, y: finalY }
  }

  const updateCloneContent = (clone: HTMLElement, testimonial: HTMLElement) => {
    const mobileSection = testimonial.querySelector('.testimonial_comp_mobile')
    if (!mobileSection) return

    const mobileText = mobileSection.querySelector('.testimonial_pg_text')
    const mobileImage = mobileSection.querySelector(
      '.testimonial_author_img img'
    ) as HTMLImageElement

    const cloneText = clone.querySelector('.testimonial_pg_text')
    const cloneImage = clone.querySelector(
      '.testimonial_author_img img'
    ) as HTMLImageElement

    if (cloneText && mobileText) {
      cloneText.textContent = mobileText.textContent
    }

    if (cloneImage && mobileImage) {
      cloneImage.src = mobileImage.src
      cloneImage.srcset = mobileImage.srcset || ''
      cloneImage.alt = mobileImage.alt || ''

      cloneImage.style.display = 'none'
      void cloneImage.offsetHeight
      cloneImage.style.display = ''
    }
  }

  const cleanupAllClones = () => {
    if (shared.clonedElement && shared.clonedElement.parentElement) {
      shared.clonedElement.remove()
      shared.clonedElement = null
    }

    if (shared.oldCloneRef && shared.oldCloneRef.parentElement) {
      shared.oldCloneRef.remove()
      shared.oldCloneRef = null
    }
  }

  const animateFollowMouse = () => {
    if (!shared.isHovering || !shared.clonedElement) return

    shared.currentX = lerp(shared.currentX, shared.mouseX, 0.1)
    shared.currentY = lerp(shared.currentY, shared.mouseY, 0.1)

    const constrained = getConstrainedPosition(shared.currentX, shared.currentY)

    gsap.set(shared.clonedElement, {
      left: constrained.x,
      top: constrained.y,
    })

    if (shared.oldCloneRef && shared.oldCloneRef.parentElement) {
      gsap.set(shared.oldCloneRef, {
        left: constrained.x,
        top: constrained.y,
      })
    }

    shared.animationFrame = requestAnimationFrame(animateFollowMouse)
  }

  const createClone = (testimonial: HTMLElement, isTransition: boolean) => {
    if (shared.isAnimating) return

    shared.isAnimating = true

    if (shared.clonedElement) {
      shared.oldCloneRef = shared.clonedElement
    }

    const newClone = templateMoreDetails.cloneNode(true) as HTMLElement

    gsap.set(newClone, {
      position: 'fixed',
      left: shared.currentX,
      top: shared.currentY,
      xPercent: -50,
      yPercent: -50,
      zIndex: 1001,
      pointerEvents: 'none',
      clipPath: 'inset(0% 0% 100% 0%)',
      opacity: 1,
      visibility: 'visible',
    })

    document.body.appendChild(newClone)
    updateCloneContent(newClone, testimonial)

    shared.clonedElement = newClone
    shared.currentTestimonial = testimonial
    updateElementDimensions()

    if (!isTransition) {
      shared.currentX = shared.mouseX
      shared.currentY = shared.mouseY
      shared.isHovering = true

      const constrained = getConstrainedPosition(
        shared.currentX,
        shared.currentY
      )
      gsap.set(newClone, {
        left: constrained.x,
        top: constrained.y,
      })
    }

    if (shared.oldCloneRef && shared.oldCloneRef.parentElement) {
      gsap.set(shared.oldCloneRef, {
        left: shared.currentX,
        top: shared.currentY,
        zIndex: 1000,
      })
    }

    gsap.to(newClone, {
      clipPath: 'inset(0% 0% 0% 0%)',
      duration: 0.6,
      ease: 'power2.out',
      onComplete: () => {
        if (shared.oldCloneRef && shared.oldCloneRef.parentElement) {
          shared.oldCloneRef.remove()
          shared.oldCloneRef = null
        }
        shared.isAnimating = false
      },
    })

    if (!isTransition) {
      animateFollowMouse()
    }
  }

  const handleMobileClick = (testimonial: HTMLElement) => {
    testimonials.forEach((t) => {
      t.classList.remove('is--active')
      const mobileWrap = t.querySelector('.testimonial_mobile_wrap')
      if (mobileWrap) {
        mobileWrap.classList.remove('is--active')
      }
    })

    testimonial.classList.add('is--active')
    const mobileWrap = testimonial.querySelector('.testimonial_mobile_wrap')
    if (mobileWrap) {
      mobileWrap.classList.add('is--active')
    }
  }

  const handleTestimonialEnter = (e: MouseEvent, testimonial: HTMLElement) => {
    shared.mouseX = e.clientX
    shared.mouseY = e.clientY

    if (shared.enterTimeout) {
      clearTimeout(shared.enterTimeout)
    }

    shared.enterTimeout = window.setTimeout(() => {
      if (shared.clonedElement && shared.currentTestimonial !== testimonial) {
        createClone(testimonial, true)
      } else if (!shared.clonedElement) {
        createClone(testimonial, false)
      }
    }, 250)
  }

  const handleTestimonialMove = (e: MouseEvent) => {
    shared.mouseX = e.clientX
    shared.mouseY = e.clientY
  }

  const handleTestimonialLeave = (e: MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement
    const isEnteringAnotherTestimonial =
      relatedTarget && testimonials.some((t) => t.contains(relatedTarget))

    if (shared.enterTimeout) {
      clearTimeout(shared.enterTimeout)
      shared.enterTimeout = null
    }

    if (isEnteringAnotherTestimonial) return

    shared.isHovering = false
    shared.currentTestimonial = null

    if (shared.animationFrame) {
      cancelAnimationFrame(shared.animationFrame)
      shared.animationFrame = null
    }

    cleanupAllClones()
    shared.isAnimating = false
  }

  onMount(() => {
    if (isDesktop) {
      const allMoreDetails = element.querySelectorAll(
        '.testimonial_more_details'
      ) as NodeListOf<HTMLElement>
      allMoreDetails.forEach((el) => {
        gsap.set(el, {
          opacity: 0,
          pointerEvents: 'none',
          visibility: 'hidden',
        })
      })

      testimonials.forEach((testimonial) => {
        testimonial.addEventListener('mouseenter', (e) =>
          handleTestimonialEnter(e, testimonial)
        )
        testimonial.addEventListener('mousemove', handleTestimonialMove)
        testimonial.addEventListener('mouseleave', handleTestimonialLeave)
      })
    } else {
      // Open first testimonial by default on mobile
      if (testimonials.length > 0) {
        handleMobileClick(testimonials[0])
      }

      testimonials.forEach((testimonial) => {
        testimonial.addEventListener('click', () =>
          handleMobileClick(testimonial)
        )
      })
    }
  })

  onDestroy(() => {
    if (shared.animationFrame) {
      cancelAnimationFrame(shared.animationFrame)
    }

    if (shared.enterTimeout) {
      clearTimeout(shared.enterTimeout)
    }

    cleanupAllClones()

    testimonials.forEach((testimonial) => {
      testimonial.removeEventListener(
        'mouseenter',
        handleTestimonialEnter as any
      )
      testimonial.removeEventListener('mousemove', handleTestimonialMove)
      testimonial.removeEventListener('mouseleave', handleTestimonialLeave)
    })

    testimonials.forEach((testimonial) => {
      testimonial.removeEventListener('click', () =>
        handleMobileClick(testimonial)
      )
    })
  })
}
